import {
  AppBar,
  Container,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  makeStyles,
  Toolbar,
  Typography,
} from "@material-ui/core";
import { ChevronLeft, Menu } from "@material-ui/icons";
import clsx from "clsx";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { bindActionCreators } from "redux";
import { ApplicationState } from "../..";
import { routesPaths } from "../../Routes";
import { ActionCreators } from "../../store/session/SessionLanguage";
import { ActionCreators as LangActionCreators } from "../../store/session/SessionLanguage";
import { SessionState } from "../../store/session/SessionState";
import { LanguageSelect } from "../common/language/LanguageSelect";
import logo from "./logo.svg";
import NavigationList from "./NavigationList";

export const drawerWidth = 240;
export const navMenuLogoHeight = 120;
export const toolbarHeight = 64;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
  },
  loginBackground: {
    backgroundColor: "#ededea",
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarLogo: {
    maxHeight: 64,
    padding: "13px",
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    padding: "0 8px",
    ...theme.mixins.toolbar,
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  menuButton: {
    padding: 0,
    marginRight: 44,
    [theme.breakpoints.down("xs")]: {
      marginRight: 36,
    },
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
  },
  content: {
    flexGrow: 1,
    height: "calc(100vh - 64px)",
    overflow: "auto",
    marginTop: "64px",
  },
  container: {
    paddingTop: theme.spacing(3),
  },
}));

type NavContainerProps = typeof ActionCreators & typeof LangActionCreators & SessionState;

const NavContainer: React.FunctionComponent<NavContainerProps> = ({ changeLanguage, children, language, detectLanguageIfPossible }) => {
  const cssClasses = useStyles({});
  const [open, setOpen] = React.useState(false);

  const location = useLocation();
  const isLogin = location.pathname === routesPaths.login();
  const showMenu = !isLogin;

  useEffect(() => {
    detectLanguageIfPossible();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDrawerOpen = () => {
    setOpen(true);
  };
  const handleDrawerClose = () => {
    setOpen(false);
  };
  return (
    <div className={clsx(cssClasses.root, isLogin && cssClasses.loginBackground)}>
      <CssBaseline />
      <AppBar position="absolute" className={clsx(cssClasses.appBar, open && cssClasses.appBarShift)}>
        <Toolbar className={cssClasses.toolbar}>
          {showMenu && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerOpen}
              className={clsx(cssClasses.menuButton, open && cssClasses.menuButtonHidden)}
            >
              <Menu />
            </IconButton>
          )}
          <img src={logo} className={cssClasses.toolbarLogo} alt="Logo of VBV AFA" />
          <Typography component="h1" variant="h6" color="inherit" noWrap className={cssClasses.title} />
          <LanguageSelect language={language} onChangeLanguage={changeLanguage} />
        </Toolbar>
      </AppBar>
      {showMenu && (
        <Drawer
          variant="permanent"
          classes={{
            paper: clsx(cssClasses.drawerPaper, !open && cssClasses.drawerPaperClose),
          }}
          open={open}
        >
          <div className={cssClasses.toolbarIcon}>
            <IconButton onClick={handleDrawerClose}>
              <ChevronLeft />
            </IconButton>
          </div>
          <Divider />
          <NavigationList />
        </Drawer>
      )}
      <main className={cssClasses.content}>
        {children && !isLogin && (
          <Container maxWidth="xl" className={cssClasses.container}>
            {children}
          </Container>
        )}
        {isLogin && children}
      </main>
    </div>
  );
};

const mapDispatchToProps = (dispatch: any) => {
  return bindActionCreators(ActionCreators, dispatch);
};

export default connect((state: ApplicationState) => state.sessionState, mapDispatchToProps, null, { pure: false })(
  NavContainer
);
